import styled from '@emotion/styled';
import { uiTheme as t } from './theme';
import FieldError from './FieldError';

export interface NumberFieldProps {
  id: string;
  value: number | null;
  onChange: (value: number | null) => void;
  suffix?: string;
  min?: number;
  max?: number;
  error?: string;
  disabled?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const Shell = styled.div<{ invalid: boolean; disabled: boolean }>(({ invalid, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '2.875rem',
  padding: '0.5rem 0.875rem',
  background: disabled ? t.color.disabledSurface : t.color.inputBg,
  border: `1px solid ${invalid ? t.color.danger : t.color.inputBorder}`,
  borderRadius: t.radius.sm,
  transition: 'border-color 0.15s ease',
  '&:focus-within': { borderColor: t.color.primary },
}));

const Bare = styled.input({
  flex: 1,
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  fontFamily: t.font,
  fontSize: '0.9375rem',
  color: t.color.titleStrong,
  padding: 0,
  '&:disabled': {
    color: t.color.disabled,
    cursor: 'not-allowed',
  },
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    margin: 0,
  },
});

const Suffix = styled.span({
  fontFamily: t.font,
  fontSize: '0.75rem',
  color: t.color.muted,
  whiteSpace: 'nowrap',
});

/**
 * Convierte el valor crudo del input al contrato del componente.
 *
 * Devuelve `null` para entradas vacías o no finitas para evitar propagar `NaN`.
 */
const parseNumberValue = (rawValue: string) => {
  if (rawValue === '') {
    return null;
  }

  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

/**
 * Campo numérico con sufijo dentro del control.
 *
 * Cuando el usuario borra el contenido entrega `null` para diferenciarlo de
 * cero y mantener el contrato del formulario.
 */
const NumberField = ({ id, value, onChange, suffix, min, max, error, disabled, ...rest }: NumberFieldProps) => {
  const describedBy =
    [rest['aria-describedby'], error ? `${id}-error` : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <>
      <Shell invalid={!!error} disabled={!!disabled}>
        <Bare
          id={id}
          type="number"
          inputMode="numeric"
          aria-label={rest['aria-label']}
          value={value === null ? '' : String(value)}
          min={min}
          max={max}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          onChange={event => onChange(parseNumberValue(event.target.value))}
        />
        {suffix && <Suffix>{suffix}</Suffix>}
      </Shell>
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </>
  );
};

export default NumberField;
